import { useMutation } from "@tanstack/react-query";
import { Button, Form, Input } from "antd";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CustomizeRequiredMark } from "~/components/common/custom-required-mark";
import { useNotification } from "~/hooks";
import { authServices } from "~/services/auth";

type FieldType = {
  password: string;
  confirmPassword: string;
};

export const ResetPasswordView = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const api = useNotification();
  const navigate = useNavigate();

  const { mutate, isPending } = useMutation({
    mutationFn: ({ password }: { password: string }) =>
      authServices.resetPassword(token!, password),
    onSuccess: () => {
      api.success({
        message: "Password Reset",
        description: "Your password has been successfully reset.",
      });
      navigate("/login");
    },
    onError: (error: any) => {
      api.error({
        message: "Reset Failed",
        description:
          error.response?.data?.message || "Invalid or expired token",
      });
    },
  });

  const onFinish = (values: FieldType) => {
    if (values.password !== values.confirmPassword) {
      api.error({
        message: "Validation Error",
        description: "Passwords do not match.",
      });
      return;
    }
    if (!token) {
      api.error({
        message: "Invalid Request",
        description: "No reset token provided.",
      });
      return;
    }
    mutate({ password: values.password });
  };

  return (
    <main className="flex-1 flex flex-col items-center justify-center px-4">
      <div className="text-center text-white mb-8 max-w-2xl">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          Set New Password
        </h1>
        <p className="text-lg text-white/80">
          Enter your new password to complete the reset process.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-8">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">New Password</h2>
          <p className="text-gray-600 mt-1">
            Create a new password for your account.
          </p>
        </div>

        <Form
          layout="vertical"
          onFinish={onFinish}
          className="space-y-6"
          requiredMark={CustomizeRequiredMark}
        >
          <Form.Item
            name="password"
            label="New Password"
            rules={[
              { required: true, message: "Please input your new password!" },
              { min: 8, message: "Password must be at least 8 characters!" },
            ]}
          >
            <Input.Password
              size="large"
              id="password"
              placeholder="Enter new password"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            rules={[
              { required: true, message: "Please confirm your password!" },
            ]}
          >
            <Input.Password
              size="large"
              id="confirmPassword"
              placeholder="Confirm new password"
            />
          </Form.Item>

          <Button
            loading={isPending}
            htmlType="submit"
            size="large"
            className="w-full bg-indigo-500 hover:bg-indigo-600 text-white py-2"
          >
            Reset Password
          </Button>

          <div className="text-center text-sm text-gray-600">
            Back to{" "}
            <Link
              to="/login"
              className="text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Sign in
            </Link>
          </div>
        </Form>
      </div>
    </main>
  );
};
